import Link from 'next/link';
import Page from '../components/page';
import { serverSideTranslations } from '../lib/serverSideTranslations';
import { useTranslation } from '../utils/i18n';

const PrivacyPolicy = () => {
  const { t } = useTranslation('common');

  return (
    <Page
      title={t('headers.privacy_page.title')}
      description={t('headers.privacy_page.description')}
    >
      <main>
        <section>
          <div className="section-content section-content--static privacy-policy">
            <h1>{t('privacy_policy.title')}</h1>

            <p>
              This privacy notice explains how NHS Property Services Limited
              (NHSPS) collects, uses, shares and protects personal data in
              connection with the NHS Open Space website and booking platform.
              It also explains your rights, how to contact us, and how to make a
              complaint about the handling of your personal data.
            </p>

            <p>
              NHSPS processes personal data in accordance with applicable UK
              data protection and privacy law, including the UK General Data
              Protection Regulation (UK GDPR), the Data Protection Act 2018, the
              Privacy and Electronic Communications Regulations 2003 (PECR), and
              any relevant amendments or successor legislation, including
              relevant provisions of the Data (Use and Access) Act 2025 (DUAA).
            </p>
            <p>
              We may update this privacy notice from time to time to reflect
              changes in the law, changes to NHS Open Space, changes to the way
              personal data is used, or changes to the organisations that
              support delivery of the service. The latest version will always be
              made available on the relevant NHS Open Space website or platform.
            </p>
            <p>
              Where we make material changes to this privacy notice, we will
              take reasonable steps to bring those changes to your attention
              where appropriate.
            </p>

            <h3>About NHS Open Space</h3>

            <p>
              NHS Open Space is a platform designed to advertise, discover, book
              and manage spaces. It enables landlords to promote available
              properties and spaces, while allowing customers and service
              providers to search for suitable space, make bookings, manage
              their accounts and, where applicable, process payments. The
              platform is operated with the support of Kajima, alongside other
              providers responsible for hosting, development, payments,
              maintenance, support and analytics.
            </p>
            <p>
              NHSPS is the controller for personal data processed for the
              purposes of operating, governing, securing and improving NHS Open
              Space where we determine the purposes and means of processing.
              Kajima and certain other suppliers support delivery of the
              platform under contractual arrangements and may process personal
              data on our behalf where they act under our instructions. Some
              service providers, including payment providers, may act as
              separate controllers for certain elements of processing where they
              determine their own purposes and means or are subject to their own
              legal and regulatory obligations.
            </p>
            <p>
              We only process personal data where we have a lawful basis for
              doing so. The main lawful bases we rely on in connection with NHS
              Open Space are:
            </p>
            <ul>
              <li>
                <strong>Contract</strong> &ndash; where processing is necessary
                to provide NHS Open Space services, manage bookings, administer
                accounts, process payments or refunds, and provide related
                support.
              </li>
              <li>
                <strong>Legitimate interests</strong> &ndash; where processing
                is necessary for our legitimate interests or those of a third
                party, provided those interests are not overridden by your
                rights and freedoms. These legitimate interests include
                operating and improving NHS Open Space, maintaining platform
                security, preventing misuse, managing service communications,
                investigating incidents, and supporting the effective
                administration of bookings and accounts.
              </li>
              <li>
                <strong>Legal obligation</strong> &ndash; where processing is
                necessary for compliance with legal, regulatory, audit,
                accounting, tax, fraud prevention or reporting obligations.
              </li>
              <li>
                <strong>Consent</strong> &ndash; where consent is required by
                law, including for certain non-essential cookies and similar
                technologies under PECR.
              </li>
              <li>
                <strong>Public task or vital interests</strong> &ndash; only
                where applicable in the particular circumstances.
              </li>
            </ul>

            <p>
              Where we rely on consent, you may withdraw it at any time.
              Withdrawal of consent will not affect the lawfulness of processing
              carried out before consent was withdrawn.
            </p>

            <h3>
              What personal information do we collect and store for NHS Open
              Space?
            </h3>

            <h3>The laws and definitions</h3>
            <p>
              You have the right to know what we are doing with your data. Where
              possible, we use simple and clear English to explain how we are
              doing this. However, sometimes we need to use certain words which
              may be difficult to understand. The list below will explain some
              of the words that we use:
            </p>
            <p>
              <span>Data subject:</span> An individual such as yourself
            </p>
            <p>
              <span>Data controller:</span> An organisation who decides how data
              is used (processed)
            </p>
            <p>
              <span>Data processor:</span> An organisation or person who
              processes data on a data controller&apos;s behalf.
            </p>
            <p>
              <span>Data protection legislation:</span> Laws which organisations
              must follow to protect and safely process your data. These laws
              are made by the UK government and the European Parliament.
            </p>
            <p>
              <span>DPA 2018:</span> Data Protection Act 2018 (UK law)
            </p>
            <p>
              <span>GDPR:</span> General Data Protection Regulation (EU law)
            </p>
            <p>
              <span>Processed or processing:</span> How we use your data. This
              includes receiving, storing, using and deleting your data.
            </p>
            <p>
              <span>Identifier:</span> Something which allows you to be
              identified. This includes anything such as your name, address or
              eye colour. An identifier could be anything if someone can tell it
              is you that is being described.
            </p>
            <p>
              <span>Anonymisation:</span> Where identifiers have been removed so
              you cannot be identified by anyone. Further information on
              anonymisation can be obtained from the&nbsp;
              <Link
                href="https://ico.org.uk/media/for-organisations/documents/1061/anonymisation-code.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Information Commissioner&apos;s website.
              </Link>
            </p>
            <p>
              <span>Pseudonymisation:</span> Where an identifier has been
              changed so only those who know how it has been changed can
              identify you.
            </p>
            <p>
              <span>We:</span> NHS Property Services Limited
            </p>

            <h3>Personal data</h3>
            <p>
              When we use your information, we will often refer to this as
              personal data. Personal data is any information which allows us or
              someone else to identify you. The most common categories of
              personal data we process are:
            </p>
            <ul>
              <li>names</li>
              <li>addresses</li>
              <li>email addresses</li>
              <li>phone numbers and extensions</li>
            </ul>

            <p>
              However, this list is not exhaustive. We understand that personal
              data can take many forms and records could include many different
              identifiers. Therefore, we look at all data on a case-by-case
              basis to decide whether the information is considered personal
              data.
            </p>
            <p>
              Data protection legislation tells us how and why we can use
              personal data of living people. Whilst data protection legislation
              does not apply to deceased persons, we maintain that we have a
              duty of confidentiality to our customers past or present.
            </p>

            <h3>Special category data</h3>
            <p>
              Some categories of personal data require additional protection
              because it is considered highly sensitive. This is called special
              category data. Open Space does not use any special category data.
            </p>

            <h3>How and why we use your personal data?</h3>
            <p>
              NHS Property Services Ltd (NHSPS) provides property and facilities
              management expertise to the NHS. We provide services centred
              around four main business areas:
            </p>

            <ul>
              <li>asset management</li>
              <li>construction project management</li>
              <li>facilities management</li>
              <li>strategic estate planning.</li>
            </ul>

            <h3>Open Space Website</h3>
            <p>
              Open Space is a booking platform jointly owned by NHSPS and Kajima
              Partnership Ltd, for which the platform is fully managed by
              Kajima. It enables Landlords to promote their properties and
              available sessional space; and provides a booking system that
              allows clients (service providers) to search for, book and pay for
              space for any Landlord properties, as well as mechanisms to manage
              their bookings and accounts. It allows Landlords visibility of all
              booking and payment transactions made in relation to their own
              specific properties. All data collected is stored on servers
              hosted by Amazon Web Services located in the UK. Payments are
              processed through our payment service providers Worldpay and
              GoCardless who store payment card details and bank details on
              their own servers also located in the UK.
            </p>
            <p>
              We take the privacy of your data very seriously and have outlined
              below how we collect data, and what we do with it. For
              clarification, NHSPS is a data controller that decides how data is
              used (processed) and Kajima, along with our Payment service
              providers, are Data processors that process data on our behalf.
            </p>
            <p>
              For us to provide a service to you, we will be required to use
              your personal data. We can process your personal data if we meet
              one or more of the following legal reasons as set out in data
              protection legislation (article 6):
            </p>

            <ul>
              <li>to fulfil a contract</li>
              <li>to comply with a legal requirement</li>
              <li>if a task is carried out in the public interest</li>
              <li>to carry out our core business functions</li>
              <li>to protection your vital interests</li>
              <li>to protect your life (e.g. emergency medical care)</li>
              <li>we have your consent</li>
            </ul>

            <h3>
              What personal information do we collect and store for Open Space?
            </h3>
            <p>In respect of customer information:</p>
            <ul>
              <li>
                When you first register to use NHS Open Space as a customer, we
                collect the contact details (name, email address and telephone
                number(s) of the primary user that has registered your
                organisation along with your organisation details.
              </li>
              <li>
                We collect similar contact details when you add further users
                (additional primary users, bill payers or bookers) to your
                organisation's customer account for the purposes of making
                bookings for your organisation or managing your organisation's
                account, and when you add host details to your account for
                associating with your bookings (a host being an individual that
                delivers the service in the rooms that your organisation has
                booked).
              </li>
              <li>
                We collect, but do not store, payment card details and/or bank
                account details that your organisation's primary or bill payer
                nominated user registers against your customer account to pay
                for your bookings. Payment card details and bank account details
                are only stored by our payment service providers in their own
                secure systems.
              </li>
              <li>
                When you add new bookings, we store financial data related to
                bookings, prices, invoices/credit notes, payments/refunds made
                and account balances.
              </li>
              <li>
                When a customer makes a payment via one of the payment provider
                options in NHS Open Space, we store the payment reference, the
                name of the person paying, and the status of the payment, which
                may include reasons as to why the payment failed.
              </li>
            </ul>

            <p>
              In connection with Landlord users of the NHS Open Space system:
            </p>

            <ul>
              <li>
                We collect and store your administrators' name and e-mail
                addresses when you add administrators to the system to maintain
                your property details, or to access and manage booking and
                financial information relating to bookings made for your
                properties
              </li>
            </ul>

            <p>In connection with all users of the NHS Open Space system:</p>

            <ul>
              <li>
                For the purposes of improving the NHS Open Space product, we
                collect information limited technical and usage information
                about use of the website or platform, such as IP address, device
                and browser information, pages viewed, referral source, session
                activity, approximate location information derived from IP
                address, and related usage data. Where this information is
                collected through cookies or similar technologies that are not
                strictly necessary, we will do so only in accordance with PECR
                and the choices you make through the cookie settings tool.
              </li>
            </ul>

            <h3>How we use your personal information</h3>

            <p>
              We use personal data submitted through NHS Open Space or generated
              through use of the service for the purposes set out in this
              notice. Depending on the circumstances, this may include:
            </p>

            <ul>
              <li>
                providing access to NHS Open Space and administering user
                accounts
              </li>
              <li>
                delivering helpdesk, customer support and account management
                services
              </li>
              <li>
                managing bookings, availability, inductions, account
                administration and related operational communications
              </li>
              <li>
                sending service-related communications, such as booking
                confirmations, account notifications, product updates and
                operational messages relevant to your use of NHS Open Space
              </li>
              <li>
                requesting optional feedback about the NHS Open Space service
              </li>
              <li>
                maintaining, securing, troubleshooting and improving the website
                and platform, including through analytics and performance
                monitoring where lawful
              </li>
            </ul>

            <p>
              Where payments are made through NHS Open Space, payment card or
              bank account details are entered directly into the secure systems
              of the relevant payment provider. NHSPS and NHS Open Space do not
              store full payment card details. We may receive limited
              payment-related information, such as payment reference, payer
              name, payment status and related transaction information, to
              manage bookings, accounts, refunds, reconciliation and support.
            </p>
            <p>
              We may also use landlord administrator details and other
              authorised user details for access management, operational
              support, account administration and related platform
              communications.
            </p>

            <ul>
              <li>
                send service-related product or platform updates relevant to
                their use of NHS Open Space and its landlord portal
              </li>
            </ul>

            <p>In connection with all users of NHS Open Space:</p>

            <ul>
              <li>
                we will only access, use, amend or disclose personal data where
                this is necessary for the purposes described in this notice,
                where instructed by the relevant controller, or where required
                or permitted by law
              </li>
              <li>
                we may generate aggregated or anonymised statistics about use of
                the service to help us manage, improve and monitor NHS Open
                Space, provided those statistics do not identify individual
                users
              </li>
            </ul>

            <h3>Cookies</h3>

            <p>
              NHS Open Space uses cookies and other storage and access
              technologies, such as pixels, scripts, tags and local storage,
              that store information on, or access information from, a user's
              device.
            </p>
            <p>
              Some of these technologies are strictly necessary to provide the
              website or platform, support security, maintain network
              management, remember essential settings, or enable services
              requested by the user. Other technologies may be used for
              analytics, service improvement, functionality, measurement,
              session insight or embedded media.
            </p>
            <p>
              Where a cookie or similar technology is not exempt under PECR, we
              will ask for consent before using it. Non-essential technologies
              are not activated until the relevant choice has been made. You can
              accept, reject or change your preferences at any time using the
              cookie settings tool made available on the website.
            </p>

            <p>
              The cookie settings tool or related cookie information describe
              the categories of technologies in use, their purposes, who
              provides them, and how long they operate for. If our use of these
              technologies changes materially, we will update the cookie
              information and, where required, request fresh consent.
            </p>

            <p>
              Where personal data is processed following the use of cookies or
              similar technologies, that processing will also be handled in
              accordance with UK GDPR and this privacy notice.
            </p>

            <p>
              This website uses the following cookies and third-party services:
            </p>

            <h4>Google Analytics</h4>

            <p>
              This website uses Google Analytics, a web analytics service
              provided by Google LLC. Google Analytics uses cookies to help the
              website analyse how users use the site. The information generated
              by the cookie about your use of the website (including your IP
              address) will be transmitted to and stored by Google on servers in
              the United States. Google will use this information for the
              purposes of evaluating your use of the website, compiling reports
              on website activity and providing other services relating to
              website activity and internet usage. Google may also transfer this
              information to third parties where required to do so by law, or
              where such third parties process the information on Google's
              behalf.
            </p>

            <h4>Google Advertising Services</h4>

            <p>
              This website uses cookies associated with Google advertising
              services, including DoubleClick, provided by Google LLC. These
              cookies are used to measure the effectiveness of advertising
              campaigns. Data may be transferred to and stored by Google on
              servers in the United States.
            </p>

            <h4>Microsoft Clarity</h4>

            <p>
              This website uses Microsoft Clarity, a behavioural analytics tool
              provided by Microsoft Corporation. Microsoft Clarity uses cookies
              to record how users interact with the website, including mouse
              movements, clicks and scrolling behaviour. This information is
              used to improve the usability, accessibility and performance of
              the website. Data collected may be transferred to and stored on
              Microsoft servers in the United States. Session recording data
              collected through Microsoft Clarity is retained for 30 days. You
              can request deletion of your data at any time by contacting us at
              dpo@property.nhs.uk
            </p>

            <h4>Vimeo</h4>
            <p>
              This website embeds video content hosted by Vimeo LLC. When you
              interact with embedded video content, Vimeo may set cookies on
              your device and collect information about your interaction with
              the video. Please refer to Vimeo's{' '}
              <a
                href="https://vimeo.com/legal/privacy/policy?msockid=12d47c3b1d6167b53a646b771cb766ff"
                target="_blank"
                rel="noopener noreferrer"
              >
                privacy policy
              </a>{' '}
              for further information.
            </p>
            <p>
              Where any of the above services require your consent, this will be
              requested via the cookie preferences tool on the website before
              any cookies are set. You may refuse non-essential cookies at any
              time by updating your preferences through the cookie tool or by
              adjusting your browser settings. However, doing so may affect the
              full functionality of this website.
            </p>

            <h3>How we protect your data</h3>
            <p>
              We are committed to protecting your data and we will always use
              your data in safe and secure ways. We take appropriate technical
              and organisational measures to protect personal data against
              accidental or unlawful destruction, loss, alteration, unauthorised
              disclosure or access. These measures are designed to ensure a
              level of security appropriate to the risk.
            </p>
            <p>
              These measures may include access controls, role-based
              permissions, security monitoring, system testing, encryption where
              appropriate, staff training, supplier due diligence, contractual
              controls, retention management and incident handling processes.
            </p>
            <p>
              We store your data on United Kingdom of Great Britain servers.
              Where possible, we will always endeavour to store your data on UK
              servers, however this is not always possible. Where we cannot
              store your data within the UK, we will endeavour to use servers
              within the (EEA) with whom the UK has an adequacy agreement that
              ensures that your data and rights are protected throughout the
              (EEA) or ensure that appropriate safeguards are in place in line
              with UK GDPR requirements — such as Standard Contractual Clauses
              or the UK-US Data Bridge — to ensure that your data and rights are
              protected. Where third-party suppliers host, maintain, support or
              secure systems used for NHS Open Space, access to personal data is
              restricted, controlled and permitted only where necessary for the
              relevant service and under appropriate contractual,
              confidentiality and security obligations.
            </p>

            <h3>How long we keep personal data</h3>
            <p>
              We keep personal data only for as long as necessary for the
              purposes for which it was collected, including to provide NHS Open
              Space, manage accounts and bookings, process payments and refunds,
              deal with support queries, meet legal, regulatory, tax, accounting
              and audit requirements, and establish, exercise or defend legal
              claims.
            </p>
            <p>
              Retention periods vary depending on the type of information and
              the purpose for which it is used. For example, account, booking,
              payment, support and technical records may be retained for
              different periods depending on operational need and legal or
              regulatory requirements. Where specific retention periods apply,
              these will be determined in accordance with our retention
              arrangements and relevant legal obligations.
            </p>
            <p>
              When personal data is no longer needed, we will securely delete
              it, anonymise it, or retain it in a form that no longer identifies
              individuals.
            </p>
            <h3>Sharing your data</h3>
            <p>
              We may share personal data, where necessary and lawful, with
              internal NHSPS teams; Kajima and other service providers that
              host, maintain, support or secure NHS Open Space; payment service
              providers; landlords; customer organisations; authorised customer
              or landlord representatives involved in managing bookings and
              access; professional advisers; auditors; insurers; regulators; law
              enforcement agencies; courts; and other parties where disclosure
              is required or permitted by law, or is necessary to establish,
              exercise or defend legal claims.
            </p>
            <p>
              Where another organisation processes personal data on our behalf,
              we require it to act only on documented instructions, keep
              personal data secure, and meet the contractual and legal
              obligations applicable to its role. Where another organisation
              acts as a separate controller, it will be responsible for
              complying with its own data protection obligations in relation to
              the personal data it controls.
            </p>
            <h3>Your rights</h3>
            <p>
              Under data protection law, you may have rights to be informed
              about how your personal data is used, to request access to your
              personal data, to ask for inaccurate data to be corrected, to
              request erasure or restriction in certain circumstances, to object
              to certain processing, and to receive personal data in a portable
              format where applicable.
            </p>
            <p>
              Where we rely on consent, you have the right to withdraw that
              consent at any time. Where we send direct marketing, you have the
              right to object to direct marketing at any time.
            </p>
            <p>
              You may also have rights in relation to significant decisions made
              solely by automated means. NHS Open Space is not intended to make
              solely automated decisions that have legal or similarly
              significant effects on individuals. If this changes, we will
              update this notice and provide the safeguards required by law,
              including information about the decision, the right to make
              representations, the right to seek human intervention, and the
              right to challenge the decision where applicable.
            </p>
            <p>
              To exercise your rights, please contact us using the details
              below. We may need to verify your identity and may ask for
              information necessary to locate the records relevant to your
              request. In some circumstances, the law allows us to refuse or
              limit a request where an exemption applies.
            </p>
            <h3>Complaints</h3>
            <p>
              If you are unhappy with how your personal data has been handled,
              you can make a complaint to us using the contact details below. We
              will handle data protection complaints in accordance with our
              complaints process and applicable legal requirements. We aim to
              acknowledge complaints and respond appropriately within the
              timescales required by law.
            </p>
            <p>
              If you remain dissatisfied after we have responded, you may
              complain to the Information Commissioner&apos;s Office.
            </p>
            <h3>Contacting us</h3>

            <p>
              NHSPS has appointed a Data Protection Officer (DPO) who can be
              contacted about this privacy notice, your rights, or any concerns
              about how personal data is handled. You can also contact the NHS
              Open Space team for operational queries about your account or use
              of the platform.
            </p>

            <p>You can contact us using the detals below:</p>

            <p>
              Data Protection Officer email:{' '}
              <a href="mailto:dpo@property.nhs.uk">dpo@property.nhs.uk</a>
            </p>
            <p>
              NHS Open Space team email:{' '}
              <a href="mailto:openspace@property.nhs.uk">
                openspace@property.nhs.uk
              </a>
            </p>
            <p>
              Postal address: NHS Property Services, 10 South Colonnade, Canary
              Wharf, London, E14 4PU.
            </p>

            <h3>Information Commissioner&apos;s Office</h3>
            <p>
              NHS Property Services is registered with the Information
              Commissioner&apos;s Office (ICO). Our registration number is:{' '}
              <span>Z3611517</span>
            </p>
            <p>
              If you are unhappy with how we have handled your personal data or
              your complaint, you can contact the ICO for independent advice or
              to raise a concern. You can also call them on 0303 123 1113.
            </p>
          </div>
        </section>
      </main>
    </Page>
  );
};

export const getServerSideProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

export default PrivacyPolicy;
